/**
 * Dialog — wrapper Radix UI, la primitive de modale du design system.
 *
 * @remarks
 * Avant SAAS-912, **huit fichiers** posaient leur propre `fixed inset-0`. Aucun
 * n'avait de piège de focus : le `Tab` sortait de la modale et continuait
 * derrière l'overlay, sur du contenu invisible, ni `inert` ni masqué aux
 * lecteurs d'écran. Trois n'avaient pas non plus de fermeture par `Échap`.
 *
 * Radix apporte d'un coup ce que personne n'écrit huit fois à la main : piège
 * de focus, `aria-modal` avec titre annoncé, arrière-plan rendu inerte,
 * restitution du focus à l'élément déclencheur, et empilement correct.
 *
 * ## Usage
 *
 * ```tsx
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogContent size="md">
 *     <DialogTitle>Supprimer ce fragment ?</DialogTitle>
 *     <DialogDescription>Cette action est irréversible.</DialogDescription>
 *     <DialogFooter>
 *       <DialogClose asChild>
 *         <Button variant="secondary">Annuler</Button>
 *       </DialogClose>
 *       <Button variant="danger" onClick={remove}>Supprimer</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 *
 * `DialogTitle` est **obligatoire** — Radix avertit en console sans lui, et une
 * modale sans nom accessible n'est annoncée que « dialogue ». S'il ne doit pas
 * être dessiné, garde-le avec `className="sr-only"` : c'est ce que fait la
 * palette de commandes, dont le champ de recherche tient le rôle de titre.
 */

import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type ReactNode,
} from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils/cn';
import { Icon } from './icon';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

/**
 * Largeurs. Les huit modales migrées tenaient en trois tailles ; `xl` existe
 * pour celle qui porte une liste scrollable (la soumission de travail).
 *
 * Deux appelants dérogent par `className` — la palette de commandes s'ancre
 * haut, et la modale de graphe occupe 84 vw parce que l'agrandissement est son
 * sujet. Une taille de plus dans l'échelle ne les aurait pas couverts.
 */
const SIZE = {
  sm: 'max-w-100',
  md: 'max-w-110',
  lg: 'max-w-140',
  xl: 'max-w-192',
} as const;

type DialogSize = keyof typeof SIZE;

type ContentProps = ComponentPropsWithoutRef<typeof RadixDialog.Content> & {
  size?: DialogSize;
  /**
   * Retire la croix de fermeture et bloque `Échap` et le clic extérieur.
   *
   * Pour une porte qu'on ne referme pas d'un geste — un consentement légal, par
   * exemple, où le seul moyen de continuer est de répondre. Sans ça, `Radix`
   * ferme au premier `Échap` et le blocage n'est plus que visuel.
   */
  blocking?: boolean;
};

export const DialogContent = forwardRef<ComponentRef<typeof RadixDialog.Content>, ContentProps>(
  function DialogContent({ className, size = 'md', blocking = false, children, ...props }, ref) {
    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="bg-overlay fixed inset-0 z-(--z-modal) data-[state=open]:animate-[fadeIn_120ms_ease-out]" />
        <RadixDialog.Content
          ref={ref}
          className={cn(
            'bg-bg border-border fixed top-1/2 left-1/2 z-(--z-modal) flex max-h-[85vh] w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-lg border p-4 shadow-lg',
            'data-[state=open]:animate-[fadeIn_120ms_ease-out]',
            SIZE[size],
            className,
          )}
          {...(blocking && {
            onEscapeKeyDown: (event: KeyboardEvent) => {
              event.preventDefault();
            },
            onPointerDownOutside: (event: { preventDefault: () => void }) => {
              event.preventDefault();
            },
            onInteractOutside: (event: { preventDefault: () => void }) => {
              event.preventDefault();
            },
          })}
          {...props}
        >
          {children}
          {!blocking && (
            <RadixDialog.Close
              aria-label="Fermer"
              className="text-fg-muted hover:bg-bg-hover hover:text-fg absolute top-3 right-3 flex size-6 items-center justify-center rounded-sm border-0 bg-transparent transition-colors"
            >
              <Icon icon={X} size="md" />
            </RadixDialog.Close>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);

type TitleProps = ComponentPropsWithoutRef<typeof RadixDialog.Title>;

export const DialogTitle = forwardRef<ComponentRef<typeof RadixDialog.Title>, TitleProps>(
  function DialogTitle({ className, ...props }, ref) {
    return (
      <RadixDialog.Title
        ref={ref}
        className={cn('text-fg text-md m-0 flex items-center gap-2 pr-6 font-semibold', className)}
        {...props}
      />
    );
  },
);

type DescriptionProps = ComponentPropsWithoutRef<typeof RadixDialog.Description>;

export const DialogDescription = forwardRef<
  ComponentRef<typeof RadixDialog.Description>,
  DescriptionProps
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <RadixDialog.Description
      ref={ref}
      className={cn('text-fg-secondary m-0 text-sm leading-relaxed', className)}
      {...props}
    />
  );
});

/** Corps scrollable — la modale ne dépasse jamais 85 vh, c'est lui qui défile. */
export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex min-h-0 flex-col overflow-y-auto', className)}>{children}</div>;
}

/** Actions, alignées à droite comme partout ailleurs dans le produit. */
export function DialogFooter({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex justify-end gap-2', className)}>{children}</div>;
}
